import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { Clock, MapPin, Phone, Mail, Shield, Check, X } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ROUTES, buildRoute } from "@/constants/routes";
import {
  usePendingKYC,
  useApproveDriver,
  useRejectDriver,
} from "../hooks/useDrivers";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageSpinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty-state";
import type { PendingDriverAPIResponse } from "@/types";

function VerificationBadge({ status }: { status: string }) {
  const variant =
    status === "approved"
      ? "success"
      : status === "rejected"
        ? "destructive"
        : "warning";
  return (
    <Badge variant={variant}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export function KYCPendingPage() {
  const navigate = useNavigate();
  const { data: pendingDrivers, isLoading } = usePendingKYC();

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedDriver, setSelectedDriver] =
    useState<PendingDriverAPIResponse | null>(null);

  const { mutate: approve, isPending: approving } = useApproveDriver();
  const { mutate: reject, isPending: rejecting } = useRejectDriver();

  const handleApprove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    approve(id);
  };

  const openRejectModal = (
    e: React.MouseEvent,
    driver: PendingDriverAPIResponse,
  ) => {
    e.stopPropagation();
    setSelectedDriver(driver);
    setRejectModal(true);
  };

  const handleReject = () => {
    if (selectedDriver && rejectReason.trim()) {
      reject(
        { id: selectedDriver.id, reason: rejectReason },
        {
          onSuccess: () => {
            setRejectModal(false);
            setRejectReason("");
            setSelectedDriver(null);
          },
        },
      );
    }
  };

  const closeModal = () => {
    setRejectModal(false);
    setRejectReason("");
    setSelectedDriver(null);
  };

  if (isLoading) {
    return <PageSpinner message="Loading pending KYC verifications..." />;
  }

  if (!pendingDrivers || pendingDrivers.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Pending KYC Verifications"
          breadcrumbs={[
            { label: "Drivers", href: ROUTES.DRIVERS },
            { label: "Pending KYC" },
          ]}
        />
        <EmptyState
          icon="drivers"
          title="No pending KYC requests"
          description="All driver KYC verifications are up to date."
          action={{
            label: "Go to Drivers",
            onClick: () => navigate(ROUTES.DRIVERS),
          }}
        />
      </div>
    );
  }

  const columns: ColumnDef<PendingDriverAPIResponse>[] = [
    {
      accessorKey: "user.name",
      header: "Driver",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={row.original.profile_image}
            alt={row.original.user.name}
            size="sm"
          />
          <div>
            <p className="font-semibold">{row.original.user.name}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Mail className="h-3 w-3" />
              {row.original.user.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "user.phone",
      header: "Phone",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          <Phone className="h-3.5 w-3.5 text-muted-foreground" />
          {row.original.user.phone}
        </div>
      ),
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm max-w-[200px]">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="truncate">{row.original.address || "—"}</span>
        </div>
      ),
    },
    {
      accessorKey: "license_number",
      header: "License",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          {row.original.license_number || "—"}
        </div>
      ),
    },
    {
      accessorKey: "verification_status",
      header: "Status",
      cell: ({ row }) => (
        <VerificationBadge status={row.original.verification_status} />
      ),
    },
    {
      accessorKey: "created_at",
      header: "Submitted",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {formatDate(row.original.created_at)}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="success"
            className="px-2 h-8"
            onClick={(e) => handleApprove(e, row.original.id)}
            disabled={approving || rejecting}
          >
            <Check className="h-4 w-4 mr-1" /> Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="px-2 h-8"
            onClick={(e) => openRejectModal(e, row.original)}
            disabled={approving || rejecting}
          >
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pending KYC Verifications"
        description={`${pendingDrivers.length} driver${pendingDrivers.length > 1 ? "s" : ""} pending verification`}
        breadcrumbs={[
          { label: "Drivers", href: ROUTES.DRIVERS },
          { label: "Pending KYC" },
        ]}
      />

      <DataTable
        columns={columns}
        data={pendingDrivers}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) =>
          navigate(buildRoute(ROUTES.KYC_DETAIL, { id: row.id }))
        }
      />

      {/* Reject Modal */}
      <Modal isOpen={rejectModal} onClose={closeModal}>
        <ModalHeader onClose={closeModal}>
          <ModalTitle>Reject KYC</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Please provide a reason for rejecting{" "}
              <strong>{selectedDriver?.user.name}</strong>&apos;s KYC
              application. The driver will be notified.
            </p>
            <div className="space-y-2">
              <Label required>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={closeModal} disabled={rejecting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            isLoading={rejecting}
            disabled={!rejectReason.trim()}
          >
            Reject KYC
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
